import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

type QuoteSummaryProponent = {
  name?: string;
  document?: string;
  personType?: 'PF' | 'PJ';
  socialName?: string;
};

type QuoteSummaryProperty = {
  propertyType?: 'house' | 'apartment';
  city?: string;
  state?: string;
  cep?: string;
  address?: string;
  number?: string;
  complement?: string;
  neighborhood?: string;
  constructionType?: string;
  propertyClassification?: string;
  insuredObjectType?: string;
  constructionTypeLabel?: string;
  propertyClassificationLabel?: string;
  insuredObjectTypeLabel?: string;
};

type QuoteSummaryCoverage = {
  id: string;
  title: string;
  amountLabel: string;
  franchiseLabel?: string;
};

type QuoteSummaryPremium = {
  quoteNumber?: string;
  purePremium?: string;
  premium?: string;
  interest?: string;
  iof?: string;
  totalPremium?: string;
  product?: string;
  status?: string;
};

type QuoteSummaryCoBroker = {
  name?: string;
  participationPercent?: string;
};

type QuoteSummaryCommercialization = {
  brokerName?: string;
  commissionPercent?: string;
  participationPercent?: string;
  coBrokers?: QuoteSummaryCoBroker[];
};

type QuoteSummaryInsurance = {
  inclusionDate?: string;
  validityStart?: string;
  validityEnd?: string;
  /** Cópia para UI do e-mail (insertByUserName) da proposta; somente leitura. */
  inclusionUser?: string;
  expirationDate?: string;
  insuranceType?: 'new' | 'renewal';
  congener?: string;
  policyNumber?: string;
};

type QuoteSummaryAssistance = {
  packageName?: string;
  assistances: string[];
  raffles: string[];
};

type QuoteSummaryState = {
  premium: QuoteSummaryPremium;
  commercialization: QuoteSummaryCommercialization;
  insurance: QuoteSummaryInsurance;
  proponent: QuoteSummaryProponent;
  property: QuoteSummaryProperty;
  coverages: QuoteSummaryCoverage[];
  assistance: QuoteSummaryAssistance;
};

type QuoteSummaryContextValue = {
  summary: QuoteSummaryState;
  updatePremiumInfo: (data: Partial<QuoteSummaryPremium>) => void;
  updateCommercialization: (
    data: Partial<QuoteSummaryCommercialization>
  ) => void;
  updateInsuranceData: (data: Partial<QuoteSummaryInsurance>) => void;
  updateProponent: (data: QuoteSummaryProponent) => void;
  updateProperty: (data: QuoteSummaryProperty) => void;
  updateCoverages: (coverages: QuoteSummaryCoverage[]) => void;
  updateAssistance: (data: Partial<QuoteSummaryAssistance>) => void;
  resetSummary: () => void;
};

const QuoteSummaryContext = createContext<QuoteSummaryContextValue | undefined>(
  undefined
);

const createInitialState = (): QuoteSummaryState => ({
  premium: {},
  commercialization: {},
  insurance: {},
  proponent: {},
  property: {},
  coverages: [],
  assistance: { assistances: [], raffles: [] },
});

export function QuoteSummaryProvider({ children }: { children: ReactNode }) {
  const [summary, setSummary] = useState<QuoteSummaryState>(createInitialState);

  const updatePremiumInfo = useCallback(
    (data: Partial<QuoteSummaryPremium>) => {
      setSummary(prev => ({
        ...prev,
        premium: {
          ...prev.premium,
          ...data,
        },
      }));
    },
    []
  );

  const updateCommercialization = useCallback(
    (data: Partial<QuoteSummaryCommercialization>) => {
      setSummary(prev => ({
        ...prev,
        commercialization: {
          ...prev.commercialization,
          ...data,
        },
      }));
    },
    []
  );

  const updateInsuranceData = useCallback(
    (data: Partial<QuoteSummaryInsurance>) => {
      setSummary(prev => ({
        ...prev,
        insurance: {
          ...prev.insurance,
          ...data,
        },
      }));
    },
    []
  );

  const updateProponent = useCallback((data: QuoteSummaryProponent) => {
    setSummary(prev => ({
      ...prev,
      proponent: {
        ...prev.proponent,
        ...data,
      },
    }));
  }, []);

  const updateProperty = useCallback((data: QuoteSummaryProperty) => {
    setSummary(prev => ({
      ...prev,
      property: {
        ...prev.property,
        ...data,
      },
    }));
  }, []);

  const updateCoverages = useCallback((coverages: QuoteSummaryCoverage[]) => {
    setSummary(prev => ({
      ...prev,
      coverages,
    }));
  }, []);

  const updateAssistance = useCallback(
    (data: Partial<QuoteSummaryAssistance>) => {
      setSummary(prev => ({
        ...prev,
        assistance: {
          ...prev.assistance,
          ...data,
        },
      }));
    },
    []
  );

  const resetSummary = useCallback(() => {
    setSummary(createInitialState());
  }, []);

  const value = useMemo(
    () => ({
      summary,
      updatePremiumInfo,
      updateCommercialization,
      updateInsuranceData,
      updateProponent,
      updateProperty,
      updateCoverages,
      updateAssistance,
      resetSummary,
    }),
    [
      summary,
      updatePremiumInfo,
      updateCommercialization,
      updateInsuranceData,
      updateProponent,
      updateProperty,
      updateCoverages,
      updateAssistance,
      resetSummary,
    ]
  );

  return (
    <QuoteSummaryContext.Provider value={value}>
      {children}
    </QuoteSummaryContext.Provider>
  );
}

export function useQuoteSummary() {
  const context = useContext(QuoteSummaryContext);
  if (!context) {
    throw new Error(
      'useQuoteSummary must be used within a QuoteSummaryProvider'
    );
  }
  return context;
}

export type {
  QuoteSummaryAssistance,
  QuoteSummaryCommercialization,
  QuoteSummaryCoBroker,
  QuoteSummaryCoverage,
  QuoteSummaryInsurance,
  QuoteSummaryPremium,
  QuoteSummaryProperty,
  QuoteSummaryProponent,
  QuoteSummaryState,
};
